<style lang="postcss">
.fade-top {
  transform: translateY(-2rem);
  @apply opacity-0;
}

.fade-bottom {
  transform: translateY(2rem);
  @apply opacity-0;
}

.fade-in {
  @apply opacity-0;
}

.fade-right {
  transform: translateX(-2rem);
  @apply opacity-0;
}

.scale-in {
  transform: scale(0);
  @apply opacity-0;
}
</style>

<script>
import tailwind from '@/design/tailwind/tailwind.config.js'

const baseTransition = ({ el, props, motionType, type }) => {
  el.classList[type](`will-change`)
  el.classList[type](`transition-property`)
  el.classList[type](`timing-${props.motion}-${motionType}`)
  el.classList[type](`duration-${props.duration}`)
}

const wait = (cb, duration = 0) => {
  const id = setTimeout(() => {
    cb()
    clearTimeout(id)
  }, duration)
}

const { durations } = tailwind.theme

const entrance = props => el => {
  if (!props.expand) {
    wait(() => {
      el.classList.remove(props.name)
    }, 0)
    return null
  }
  const width = getComputedStyle(el).width

  el.style.width = width
  el.style.position = 'absolute'
  el.style.visibility = 'hidden'
  el.style.height = 'auto'

  const height = getComputedStyle(el).height

  el.style.width = null
  el.style.position = null
  el.style.visibility = null
  el.style.height = 0

  getComputedStyle(el).height

  wait(() => {
    el.style.height = height
    el.classList.remove(props.name)
  }, 0)
}

export default {
  name: 'AppTransition',

  functional: true,

  props: {
    expand: Boolean,
    name: {
      type: String,
      default: () => 'fade-top',
      validator: name =>
        [
          'fade-top',
          'fade-bottom',
          'fade-in',
          'scale-in',
          'fade-right'
        ].includes(name)
    },
    motion: {
      type: String,
      default: () => 'productive',
      validator: motion => ['productive', 'expressive'].includes(motion)
    },
    duration: {
      type: String,
      default: () => 'fast-1',
      validator: duration => Object.keys(durations).includes(duration)
    },
    afterEnter: {
      type: Function,
      default: () => () => {}
    },
    afterLeave: {
      type: Function,
      default: () => () => {}
    },
    afterAppear: {
      type: Function,
      default: () => () => {}
    },
    beforeEnter: {
      type: Function,
      default: () => () => {}
    },
    beforeLeave: {
      type: Function,
      default: () => () => {}
    },
    beforeAppear: {
      type: Function,
      default: () => () => {}
    }
  },

  render: (h, { children, props }) => {
    const data = {
      props: {
        mode: 'out-in',
        appear: props.appear
      },

      on: {
        beforeEnter(el) {
          el.style.overflow = 'hidden'
          el.classList.add(props.name)
          baseTransition({ el, props, motionType: 'enter', type: 'add' })
          props.beforeEnter()
        },

        beforeLeave(el) {
          el.style.overflow = 'hidden'
          baseTransition({ el, props, motionType: 'leave', type: 'add' })
          props.beforeLeave()
        },

        beforeAppear(el) {
          el.style.overflow = 'hidden'
          el.classList.add(props.name)
          baseTransition({ el, props, motionType: 'enter', type: 'add' })
          props.beforeAppear()
        },

        enter: entrance(props),
        appear: entrance(props),

        leave(el) {
          el.classList.add(props.name)

          if (!props.expand) return

          const height = getComputedStyle(el).height

          el.style.height = height

          getComputedStyle(el).height

          wait(() => {
            el.style.height = 0
          })
        },

        afterEnter(el) {
          el.style.height = null
          el.style.width = null
          el.style.overflow = null
          baseTransition({ el, props, motionType: 'enter', type: 'remove' })
          props.afterEnter()
        },

        afterLeave(el) {
          el.style.overflow = null
          baseTransition({ el, props, motionType: 'leave', type: 'remove' })
          props.afterLeave()
        },

        afterAppear(el) {
          el.style.overflow = null
          baseTransition({ el, props, motionType: 'enter', type: 'remove' })
          props.afterAppear()
        }
      }
    }

    return h('transition', data, children)
  }
}
</script>
